import { useEffect, useRef } from "react";
import { useNavigate, type NavigateFunction } from "react-router-dom";
import { useVoice } from "@lib/voiceContext";
import type { UseTwilioVoice } from "@hooks/useTwilioVoice";

/**
 * Shapes the service worker (src/sw/index.ts) posts to window clients:
 *  - { type: "answer-call" } / { type: "decline-call" }  (from SW `message` relay)
 *  - { type: "push-received", eventType }                 (from the `push` handler,
 *    after showNotification; eventType is the push payload's data.kind)
 *  - { type: "notification-action", action, data }        (from `notificationclick`)
 *    where `action` is the notification button id ("answer" | "decline" | "reply"
 *    | "mark-read" | "" for a plain click) and `data` carries { url, kind, callId, phone }.
 */
interface NotificationActionData {
  url?: string;
  kind?: string;
  callId?: string;
  phone?: string;
}

interface SwClientMessage {
  type?: string;
  action?: string;
  eventType?: string;
  data?: NotificationActionData;
}

const MESSAGES_PATH = "/dashboard/messages";
const CALLS_PATH = "/dashboard/calls";

/** Convert an SW-provided url (absolute or path) into a same-origin in-app path. */
function toInAppPath(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    return u.pathname + u.search;
  } catch {
    return url.startsWith("/") ? url : null;
  }
}

function messagesPathFor(phone: string | undefined): string {
  return phone ? `${MESSAGES_PATH}?phone=${encodeURIComponent(phone)}` : MESSAGES_PATH;
}

function handleMessage(
  msg: SwClientMessage,
  voice: UseTwilioVoice,
  navigate: NavigateFunction,
): void {
  const type = msg.type;

  // Direct call-control relays (also reachable via a "voice-incoming" notification).
  if (type === "answer-call") {
    voice.answer();
    return;
  }
  if (type === "decline-call") {
    voice.reject();
    return;
  }

  // A push arrived while the app is open: re-broadcast as a window event so
  // data hooks (messages, calls) can refresh without new infrastructure.
  if (type === "push-received") {
    window.dispatchEvent(
      new CustomEvent("aria:push", { detail: { eventType: msg.eventType ?? "generic" } })
    );
    return;
  }

  if (type !== "notification-action") return;

  const action = msg.action ?? "";
  const data = msg.data ?? {};

  // Incoming-call notification buttons.
  if (data.kind === "voice-incoming" || action === "answer" || action === "decline") {
    if (action === "answer") voice.answer();
    else if (action === "decline") voice.reject();
    else navigate(CALLS_PATH);
    return;
  }

  // Messaging notification (reply / mark-read) or anything carrying a peer number.
  if (
    action === "reply" ||
    action === "mark-read" ||
    data.kind === "sms" ||
    data.kind === "whatsapp" ||
    data.phone
  ) {
    navigate(messagesPathFor(data.phone));
    return;
  }

  // Generic click: follow the notification's own url, else land on the dashboard.
  navigate(toInAppPath(data.url) ?? "/dashboard");
}

/**
 * Listens for messages the service worker posts to clients and turns them into
 * in-app actions (answer/decline a call) or navigation. Must be mounted inside
 * the VoiceProvider so `useVoice()` resolves, and inside the Router for `navigate`.
 */
export function useServiceWorkerMessages(): void {
  const voice = useVoice();
  const navigate = useNavigate();

  // Keep the listener registered once while always calling the latest handlers.
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    const onMessage = (event: MessageEvent): void => {
      const msg = (event.data ?? {}) as SwClientMessage;
      if (!msg || typeof msg !== "object") return;
      handleMessage(msg, voiceRef.current, navigateRef.current);
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);
}
